import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { api, API, formatErr } from "@/lib/api";
import { SITE } from "@/config/site";
import { fmtNGN } from "@/lib/finance";
import {
  Bank, GraduationCap, CheckCircle, CircleNotch, Trash, ArrowRight,
  CaretLeft, Receipt, Copy, Warning, Plus, BookmarkSimple, DownloadSimple,
} from "@phosphor-icons/react";

/**
 * Pay School Fees — four-step wizard:
 *   1. Pick a saved school OR enter bank + account manually
 *   2. Resolve the account name and confirm the destination
 *   3. Enter amount + child's full name (required)
 *   4. Review → pay via Paystack → verify → receipt
 *
 * We never hold funds on the platform. The parent is charged via Paystack
 * Checkout; on success the backend fires a Paystack Transfer to the school's
 * NUBAN. The parent sees a unique AR reference on the receipt.
 */

const STEPS = ["destination", "verify", "amount", "review", "receipt"];

export default function ParentPaySchool() {
  const [params] = useSearchParams();
  const [step, setStep] = useState("destination");
  const [banks, setBanks] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);

  // Destination state (populated either from a saved school or manual entry)
  const [dest, setDest] = useState({
    bank_code: "",
    bank_name: "",
    account_number: "",
    school_name: "",
    account_name: "",
    saved_from_id: null,
  });
  const [amount, setAmount] = useState("");
  const [childName, setChildName] = useState("");
  const [saveForNextTime, setSaveForNextTime] = useState(true);

  const [resolving, setResolving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [receipt, setReceipt] = useState(null);

  // Load banks + saved schools once
  useEffect(() => {
    (async () => {
      try {
        const [b, s] = await Promise.all([
          api.get("/banks"),
          api.get("/payments/saved-schools"),
        ]);
        setBanks(b.data || []);
        setSaved(s.data || []);
      } finally { setLoadingLists(false); }
    })();
  }, []);

  // If Paystack redirected back with ?reference=..., jump straight to verify + receipt
  useEffect(() => {
    const ref = params.get("reference") || params.get("trxref");
    if (ref) verifyPayment(ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadSaved = async () => {
    const s = await api.get("/payments/saved-schools");
    setSaved(s.data || []);
  };

  const pickSaved = (sch) => {
    setDest({
      bank_code: sch.bank_code,
      bank_name: sch.bank_name,
      account_number: sch.account_number,
      school_name: sch.school_name,
      account_name: sch.account_name,
      saved_from_id: sch.id,
    });
    setStep("amount"); // skip account resolution for saved schools
  };

  const deleteSaved = async (sch) => {
    if (!window.confirm(`Remove ${sch.school_name} from your saved schools?`)) return;
    try {
      await api.delete(`/payments/saved-schools/${sch.id}`);
      toast.success("Saved school removed");
      await reloadSaved();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const resolveAccount = async () => {
    setResolving(true);
    try {
      const { data } = await api.post("/payments/resolve-account", {
        bank_code: dest.bank_code,
        account_number: dest.account_number,
      });
      setDest((d) => ({ ...d, account_name: data.account_name }));
      setStep("verify");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setResolving(false); }
  };

  const initiatePayment = async () => {
    const amt = Number(amount);
    if (!amt || amt < 500) return toast.error("Enter an amount of at least ₦500");
    if (!childName.trim()) return toast.error("Please enter the child's full name");

    setPaying(true);
    try {
      // Save the school for next time (fire-and-forget; it's a convenience)
      if (saveForNextTime && !dest.saved_from_id) {
        try {
          await api.post("/payments/saved-schools", {
            school_name: dest.school_name || dest.account_name,
            bank_code: dest.bank_code,
            bank_name: dest.bank_name,
            account_number: dest.account_number,
            account_name: dest.account_name,
          });
          reloadSaved();
        } catch { /* non-fatal */ }
      }

      const callback_url = `${window.location.origin}/parent/pay`;
      const { data } = await api.post("/payments/direct-pay/initiate", {
        school_name: dest.school_name || dest.account_name,
        bank_code: dest.bank_code,
        bank_name: dest.bank_name,
        account_number: dest.account_number,
        account_name: dest.account_name,
        amount: amt,
        child_name: childName.trim(),
        callback_url,
      });
      // Hand off to Paystack hosted checkout
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
      setPaying(false);
    }
  };

  const verifyPayment = async (reference) => {
    setStep("receipt");
    setPaying(true);
    try {
      const { data } = await api.post("/payments/direct-pay/verify", { reference });
      setReceipt(data.payment || { reference, status: data.status });
      if (data.ok && data.status === "paid") toast.success("Payment settled to the school");
      else if (data.ok && data.status === "charged") toast.success("Payment received — settling to the school");
      else if (data.ok && data.status === "settling") toast.info("Payment received — awaiting bank confirmation");
      else toast.error(data.reason || "Payment could not be verified");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setPaying(false); }
  };

  const resetAll = () => {
    setDest({ bank_code: "", bank_name: "", account_number: "", school_name: "", account_name: "", saved_from_id: null });
    setAmount("");
    setChildName("");
    setReceipt(null);
    setStep("destination");
    window.history.replaceState({}, document.title, "/parent/pay");
  };

  return (
    <DashboardShell title="Pay school fees" subtitle="Pay a school directly, from your card into their bank account. No wallet, no top-ups.">
      <div className="max-w-3xl">
        <Stepper step={step} />

        {step === "destination" && (
          <DestinationStep
            banks={banks}
            saved={saved}
            loading={loadingLists}
            dest={dest}
            setDest={setDest}
            onPickSaved={pickSaved}
            onDeleteSaved={deleteSaved}
            onNext={resolveAccount}
            resolving={resolving}
          />
        )}

        {step === "verify" && (
          <VerifyStep
            dest={dest}
            setDest={setDest}
            onConfirm={() => setStep("amount")}
            onBack={() => setStep("destination")}
          />
        )}

        {step === "amount" && (
          <AmountStep
            dest={dest}
            amount={amount}
            setAmount={setAmount}
            childName={childName}
            setChildName={setChildName}
            onNext={() => setStep("review")}
            onBack={() => setStep(dest.saved_from_id ? "destination" : "verify")}
          />
        )}

        {step === "review" && (
          <ReviewStep
            dest={dest}
            amount={amount}
            childName={childName}
            saveForNextTime={saveForNextTime}
            setSaveForNextTime={setSaveForNextTime}
            paying={paying}
            onPay={initiatePayment}
            onBack={() => setStep("amount")}
          />
        )}

        {step === "receipt" && (
          <ReceiptStep receipt={receipt} paying={paying} onDone={resetAll} />
        )}
      </div>
    </DashboardShell>
  );
}

/* ─────────── Step indicator ─────────── */
function Stepper({ step }) {
  const idx = STEPS.indexOf(step);
  return (
    <ol className="flex items-center gap-2 text-xs text-[#4F5D4F] mb-6 overflow-x-auto">
      {["Destination", "Verify", "Amount", "Review", "Receipt"].map((label, i) => (
        <li key={label} className="flex items-center gap-2 shrink-0">
          <span
            className={`w-6 h-6 rounded-full grid place-items-center font-mono text-[11px] font-semibold ${
              i < idx ? "bg-[#0A3622] text-white" :
              i === idx ? "bg-[#F2B632] text-[#0A3622]" :
              "bg-[#E5E3DB] text-[#4F5D4F]"
            }`}
            data-testid={`pay-step-${i + 1}`}
          >
            {i < idx ? "✓" : i + 1}
          </span>
          <span className={i === idx ? "font-semibold text-[#132A13]" : ""}>{label}</span>
          {i < STEPS.length - 1 && <span className="text-[#E5E3DB] mx-1">—</span>}
        </li>
      ))}
    </ol>
  );
}

/* ─────────── Step 1 — Destination ─────────── */
function DestinationStep({ banks, saved, loading, dest, setDest, onPickSaved, onDeleteSaved, onNext, resolving }) {
  const canNext = dest.bank_code && /^\d{10}$/.test(dest.account_number);
  return (
    <div className="space-y-6">
      {saved.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#E5E3DB] p-5" data-testid="saved-schools-section">
          <div className="flex items-center gap-2 mb-4">
            <BookmarkSimple size={18} weight="fill" className="text-[#0A3622]" />
            <h3 className="font-display text-lg font-medium text-[#132A13]">Saved schools</h3>
            <span className="text-xs text-[#4F5D4F] ml-auto">Tap to use</span>
          </div>
          <ul className="divide-y divide-[#E5E3DB]">
            {saved.map((s) => (
              <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`saved-school-${s.id}`}>
                <div className="w-10 h-10 rounded-xl bg-[#F0EFEA] grid place-items-center text-[#0A3622] shrink-0">
                  <GraduationCap size={20} weight="duotone" />
                </div>
                <button
                  type="button"
                  onClick={() => onPickSaved(s)}
                  className="flex-1 text-left min-w-0"
                  data-testid={`saved-school-pick-${s.id}`}
                >
                  <div className="font-semibold text-[#132A13] truncate">{s.school_name}</div>
                  <div className="text-xs text-[#4F5D4F] font-mono truncate">
                    {s.bank_name} · {s.account_number} · {s.account_name}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteSaved(s)}
                  className="p-2 text-[#C93B3B] hover:bg-[#FBE3E3] rounded-lg"
                  aria-label={`Delete ${s.school_name}`}
                  data-testid={`saved-school-delete-${s.id}`}
                >
                  <Trash size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E5E3DB] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Plus size={18} weight="bold" className="text-[#0A3622]" />
          <h3 className="font-display text-lg font-medium text-[#132A13]">
            {saved.length ? "Pay a new school" : "Enter the school's bank details"}
          </h3>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="overline">Bank</span>
            <select
              className="input mt-1"
              value={dest.bank_code}
              onChange={(e) => {
                const b = banks.find((x) => x.code === e.target.value);
                setDest((d) => ({ ...d, bank_code: e.target.value, bank_name: b?.name || "" }));
              }}
              disabled={loading}
              data-testid="pay-bank-select"
            >
              <option value="">{loading ? "Loading banks…" : "Select bank…"}</option>
              {banks.map((b) => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="overline">Account number</span>
            <input
              className="input mt-1 font-mono"
              inputMode="numeric"
              maxLength={10}
              placeholder="10 digits"
              value={dest.account_number}
              onChange={(e) => setDest((d) => ({ ...d, account_number: e.target.value.replace(/\D/g, "") }))}
              data-testid="pay-account-number"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={onNext}
          disabled={!canNext || resolving}
          className="btn-primary mt-5"
          data-testid="pay-resolve-btn"
        >
          {resolving ? <><CircleNotch className="animate-spin" size={16} /> Looking up account…</> : <>Look up account <ArrowRight size={16} weight="bold" /></>}
        </button>
      </div>
    </div>
  );
}

/* ─────────── Step 2 — Verify resolved account ─────────── */
function VerifyStep({ dest, setDest, onConfirm, onBack }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5E3DB] p-6" data-testid="pay-verify-step">
      <div className="flex items-start gap-3">
        <CheckCircle size={22} weight="fill" className="text-[#0A3622] mt-0.5" />
        <div>
          <h3 className="font-display text-xl text-[#132A13] font-medium">We found the account</h3>
          <p className="text-sm text-[#4F5D4F] mt-1">
            Please double-check this is the right school before you continue.
          </p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
        <dt className="text-[#4F5D4F]">Account name</dt>
        <dd className="font-semibold text-[#132A13]" data-testid="verify-account-name">{dest.account_name}</dd>
        <dt className="text-[#4F5D4F]">Bank</dt>
        <dd className="font-semibold text-[#132A13]">{dest.bank_name}</dd>
        <dt className="text-[#4F5D4F]">Account number</dt>
        <dd className="font-mono text-[#132A13]">{dest.account_number}</dd>
      </dl>

      <label className="block mt-5">
        <span className="overline">School name (how you'd like to see it saved)</span>
        <input
          className="input mt-1"
          value={dest.school_name}
          onChange={(e) => setDest((d) => ({ ...d, school_name: e.target.value }))}
          placeholder={dest.account_name}
          data-testid="verify-school-name"
        />
      </label>

      <div className="mt-6 flex items-center gap-3">
        <button type="button" onClick={onBack} className="btn-ghost" data-testid="verify-back">
          <CaretLeft size={14} weight="bold" /> Back
        </button>
        <button type="button" onClick={onConfirm} className="btn-primary" data-testid="verify-confirm">
          Yes, this is the right account <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

/* ─────────── Step 3 — Amount + child's name ─────────── */
function AmountStep({ dest, amount, setAmount, childName, setChildName, onNext, onBack }) {
  const canNext = Number(amount) >= 500 && childName.trim().length >= 2;
  return (
    <div className="rounded-2xl bg-white border border-[#E5E3DB] p-6" data-testid="pay-amount-step">
      <h3 className="font-display text-xl text-[#132A13] font-medium">How much, and for which child?</h3>
      <p className="text-sm text-[#4F5D4F] mt-1">
        Paying <span className="font-semibold text-[#132A13]">{dest.school_name || dest.account_name}</span>.
      </p>

      <label className="block mt-5">
        <span className="overline">Amount (NGN)</span>
        <input
          type="number"
          min="500"
          step="100"
          className="input mt-1 font-mono"
          placeholder="150000"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="pay-amount-input"
        />
      </label>

      <label className="block mt-4">
        <span className="overline">Remarks · Child's full name <span className="text-[#C93B3B]">*</span></span>
        <input
          type="text"
          className="input mt-1"
          placeholder="e.g. Chidinma Okafor"
          maxLength={120}
          value={childName}
          onChange={(e) => setChildName(e.target.value)}
          data-testid="pay-child-name"
          required
        />
        <p className="text-xs text-[#4F5D4F] mt-1">
          This appears on the school's bank statement so they know which pupil the payment is for.
        </p>
      </label>

      <div className="mt-6 flex items-center gap-3">
        <button type="button" onClick={onBack} className="btn-ghost" data-testid="amount-back">
          <CaretLeft size={14} weight="bold" /> Back
        </button>
        <button type="button" onClick={onNext} disabled={!canNext} className="btn-primary" data-testid="amount-next">
          Review payment <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

/* ─────────── Step 4 — Review ─────────── */
function ReviewStep({ dest, amount, childName, saveForNextTime, setSaveForNextTime, paying, onPay, onBack }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5E3DB] p-6" data-testid="pay-review-step">
      <h3 className="font-display text-xl text-[#132A13] font-medium">Review and pay</h3>
      <p className="text-sm text-[#4F5D4F] mt-1">
        You'll be taken to Paystack's secure checkout to complete your card payment.
      </p>

      <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm">
        <dt className="text-[#4F5D4F]">School</dt>
        <dd className="font-semibold text-[#132A13]" data-testid="review-school">{dest.school_name || dest.account_name}</dd>
        <dt className="text-[#4F5D4F]">Bank</dt>
        <dd className="text-[#132A13]">{dest.bank_name}</dd>
        <dt className="text-[#4F5D4F]">Account number</dt>
        <dd className="font-mono text-[#132A13]" data-testid="review-acct">{dest.account_number}</dd>
        <dt className="text-[#4F5D4F]">Account name</dt>
        <dd className="text-[#132A13]">{dest.account_name}</dd>
        <dt className="text-[#4F5D4F]">Amount</dt>
        <dd className="font-display text-xl text-[#132A13]" data-testid="review-amount">{fmtNGN(Number(amount))}</dd>
        <dt className="text-[#4F5D4F]">For (child's name)</dt>
        <dd className="font-semibold text-[#132A13]" data-testid="review-child">{childName}</dd>
      </dl>

      {!dest.saved_from_id && (
        <label className="flex items-start gap-2 mt-5 text-sm cursor-pointer" data-testid="review-save-checkbox">
          <input
            type="checkbox"
            checked={saveForNextTime}
            onChange={(e) => setSaveForNextTime(e.target.checked)}
            className="mt-1 accent-[#0A3622]"
          />
          <span className="text-[#4F5D4F]">Save this school so I don't have to type it next time.</span>
        </label>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button type="button" onClick={onBack} disabled={paying} className="btn-ghost">
          <CaretLeft size={14} weight="bold" /> Back
        </button>
        <button
          type="button"
          onClick={onPay}
          disabled={paying}
          className="btn-primary"
          data-testid="review-pay-btn"
        >
          {paying
            ? <><CircleNotch className="animate-spin" size={16} /> Opening Paystack…</>
            : <>Pay {fmtNGN(Number(amount))} securely <ArrowRight size={16} weight="bold" /></>
          }
        </button>
      </div>
    </div>
  );
}

/* ─────────── Step 5 — Receipt ─────────── */
function ReceiptHeader() {
  return (
    <div className="flex items-start justify-between gap-4 pb-3 border-b-2 border-[#0A3622]" data-testid="receipt-header">
      <img src="/brand/logo-document.png" alt="Ascentremit" width="180" className="block w-[180px] h-auto shrink-0" />
      <div className="text-right text-[11px] leading-snug text-[#5B6B62]">
        <div>{SITE.COMPANY_LEGAL_NAME}</div>
        <div>{SITE.CAC_RC_NUMBER}</div>
        <div>{SITE.REGISTERED_ADDRESS}</div>
        <div>{SITE.SUPPORT_EMAIL}</div>
      </div>
    </div>
  );
}

function ReceiptStep({ receipt, paying, onDone }) {
  if (paying || !receipt) {
    return (
      <div className="rounded-2xl bg-white border border-[#E5E3DB] p-10 text-center" data-testid="pay-receipt-loading">
        <CircleNotch size={32} className="animate-spin mx-auto text-[#0A3622]" />
        <p className="mt-4 text-sm text-[#4F5D4F]">Verifying your payment with Paystack…</p>
      </div>
    );
  }

  const status = receipt.status;
  const paid = status === "paid";
  const charged = status === "charged" || status === "settling";
  const failed = status === "failed";

  return (
    <div className="rounded-2xl bg-white border border-[#E5E3DB] p-6" data-testid="pay-receipt-step">
      <div className="text-center">
        <div className={`w-16 h-16 rounded-full grid place-items-center mx-auto ${
          paid ? "bg-[#E2F3EB] text-[#0A3622]" :
          charged ? "bg-[#FBF1DC] text-[#B45309]" :
          "bg-[#FBE3E3] text-[#C93B3B]"
        }`}>
          {paid ? <CheckCircle size={34} weight="fill" />
            : charged ? <Receipt size={30} weight="fill" />
            : <Warning size={30} weight="fill" />}
        </div>
        <h3 className="font-display text-2xl font-medium text-[#132A13] mt-4">
          {paid ? "Payment settled to the school"
            : charged ? "Payment received — settling"
            : failed ? "Payment failed"
            : "Payment status"}
        </h3>
        <p className="text-sm text-[#4F5D4F] mt-1">
          {paid ? "The school's bank account has been credited."
            : charged ? "Your card has been charged. Paystack is transferring the funds to the school now — this usually takes a few minutes."
            : "No charge went through. You can try again."}
        </p>
      </div>

      <div className="mt-6 border border-[#E5E3DB] rounded-xl p-5 bg-white" data-testid="receipt-document">
        <ReceiptHeader />
        <h4 className="font-display text-lg font-semibold text-[#0A3622] mt-4">Payment receipt</h4>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm bg-[#F9F7F4] rounded-xl p-4">
        <dt className="text-[#4F5D4F]">Reference</dt>
        <dd className="font-mono text-[#132A13] flex items-center gap-2" data-testid="receipt-reference">
          {receipt.reference}
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(receipt.reference); toast.success("Copied"); }}
            className="text-[#0A3622] hover:bg-[#E2F3EB] p-1 rounded"
            aria-label="Copy reference"
          >
            <Copy size={14} />
          </button>
        </dd>
        {receipt.school_name && <><dt className="text-[#4F5D4F]">School</dt><dd className="text-[#132A13]">{receipt.school_name}</dd></>}
        {receipt.account_number && <><dt className="text-[#4F5D4F]">Account</dt><dd className="font-mono text-[#132A13]">{receipt.account_number} · {receipt.bank_name}</dd></>}
        {receipt.amount && <><dt className="text-[#4F5D4F]">Amount</dt><dd className="font-display text-lg text-[#132A13]">{fmtNGN(receipt.amount)}</dd></>}
        {receipt.child_name && <><dt className="text-[#4F5D4F]">For</dt><dd className="text-[#132A13]">{receipt.child_name}</dd></>}
        {receipt.transfer_code && <><dt className="text-[#4F5D4F]">Transfer code</dt><dd className="font-mono text-[#4F5D4F] text-xs">{receipt.transfer_code}</dd></>}
      </dl>
        <div className="mt-4 text-xs text-[#5B6B62] leading-relaxed" data-testid="receipt-footer">
          <p>Card and bank payments are processed by Paystack Payments Ltd.</p>
          <p>Support: {SITE.SUPPORT_EMAIL} · WhatsApp {SITE.WHATSAPP}</p>
          <p>Keep this receipt for your records.</p>
        </div>
      </div>

      {(paid || charged) && (
        <a
          href={`${API}/payments/direct-pay/${encodeURIComponent(receipt.reference)}/receipt.pdf`}
          target="_blank" rel="noreferrer"
          className="btn-outline mt-4 w-full justify-center"
          data-testid="receipt-download-pdf"
        >
          <DownloadSimple size={16} weight="bold" /> Download PDF receipt
        </a>
      )}

      <button type="button" onClick={onDone} className="btn-primary mt-6 w-full justify-center" data-testid="receipt-done">
        Make another payment <ArrowRight size={16} weight="bold" />
      </button>
    </div>
  );
}
